'use client'

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Loader2, Trash2, Plus, ArrowRight } from 'lucide-react'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





interface TenantAssignment {
  tenant_id: string
  tenant_name: string
  reserved_concurrency: number
  reserved_agents: number
  real_agents: number
  has_pending_upgrade: boolean
  pending_concurrency: number | null
  pending_agents: number | null
  assigned_at: string
}

interface AccountDetail {
  id: string
  name: string
  max_concurrency: number
  max_agents: number
  used_concurrency: number
  used_agents: number
  available_concurrency: number
  available_agents: number
}

interface TenantOption {
  id: string
  name: string
  current_account_id?: string
  current_account_name?: string
}

interface TenantAssignmentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string | null
  accountName: string
  onChanged: () => void
}

export function TenantAssignmentsDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
  onChanged,
}: TenantAssignmentsDialogProps) {


  const tUi = useTranslations("Billing.BillingTenantAssignmentsDialog")

  const { fetchWithAuth } = useAuth()
  const [account, setAccount] = useState<AccountDetail | null>(null)
  const [assignments, setAssignments] = useState<TenantAssignment[]>([])
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Assign form state (reserved slots are derived server-side from the plan)
  const [selectedTenantId, setSelectedTenantId] = useState('')

  const fetchAssignments = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/admin/billing/elevenlabs/accounts/${accountId}`)
      if (!res.ok) throw new Error('Failed to fetch account details')

      const data = await res.json()
      setAccount(data.account)
      setAssignments(data.tenants || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("unknownError"))
    } finally {
      setLoading(false)
    }
  }, [accountId, fetchWithAuth, tUi])

  const fetchTenants = useCallback(async () => {
    try {
      const [tenantsRes, accountsRes] = await Promise.all([
        fetchWithAuth('/api/tenants'),
        fetchWithAuth('/api/admin/billing/elevenlabs/accounts'),
      ])

      if (!tenantsRes.ok) return

      const tenantsData = await tenantsRes.json()
      const accountsData = accountsRes.ok ? await accountsRes.json() : { tenantAssignmentMap: {} }
      const assignmentMap = (accountsData.tenantAssignmentMap || {}) as Record<
        string,
        { account_id: string; account_name: string }
      >

      const options: TenantOption[] = (tenantsData.tenants || []).map(
        (t: { id: string; name: string }) => ({
          id: t.id,
          name: t.name,
          current_account_id: assignmentMap[t.id]?.account_id,
          current_account_name: assignmentMap[t.id]?.account_name,
        })
      )

      setTenantOptions(options)
    } catch {
      // Non-critical — tenant list is for the assign form
    }
  }, [fetchWithAuth])

  useEffect(() => {
    if (open && accountId) {
      fetchAssignments()
      fetchTenants()
      setSelectedTenantId('')
    }
  }, [open, accountId, fetchAssignments, fetchTenants])

  const handleAssign = async () => {
    if (!accountId || !selectedTenantId) return
    setSaving(true)
    setError(null)

    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/assignments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: selectedTenantId,
          }),
        }
      )

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to assign tenant')
      }

      setSelectedTenantId('')
      await fetchAssignments()
      await fetchTenants()
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToAssign"))
    } finally {
      setSaving(false)
    }
  }

  const handleUnassign = async (tenantId: string) => {
    if (!accountId) return
    setSaving(true)
    setError(null)

    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/assignments/${tenantId}`,
        { method: 'DELETE' }
      )

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to unassign tenant')
      }

      await fetchAssignments()
      await fetchTenants()
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToUnassign"))
    } finally {
      setSaving(false)
    }
  }

  // Filter tenant options: exclude tenants already assigned to THIS account
  const assignedTenantIds = new Set(assignments.map((a) => a.tenant_id))
  const availableTenants = tenantOptions.filter((t) => !assignedTenantIds.has(t.id))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span><span key={tUi("tenantAssignments")}>{tUi("tenantAssignments")}</span></span> {accountName}</DialogTitle>
          <DialogDescription>
            <span><span key={tUi("manageWhichTenantsAreAssignedToThisElevenLabs")}>{tUi("manageWhichTenantsAreAssignedToThisElevenLabs")}</span></span>
            {account && (
              <span className="ml-2"><TranslationText>{tUi.rich("capacityValue0Value1ConcValue2Value3Value4Agents", { value0: String(account.used_concurrency), value1: String(account.max_concurrency), value2: String(' '), value3: String(account.used_agents), value4: String(account.max_agents) })}</TranslationText></span>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Current assignments */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium"><span><span key={tUi("assignedTenants")}>{tUi("assignedTenants")}</span></span>{assignments.length}<span>)</span></h4>
              {assignments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2"><span key={tUi("noTenantsAssigned")}>{tUi("noTenantsAssigned")}</span></p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead><span key={tUi("tenant")}>{tUi("tenant")}</span></TableHead>
                      <TableHead><span key={tUi("concurrency")}>{tUi("concurrency")}</span></TableHead>
                      <TableHead><span key={tUi("agents")}>{tUi("agents")}</span></TableHead>
                      <TableHead className="w-[60px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((a) => (
                      <TableRow key={a.tenant_id}>
                        <TableCell>
                          <div className="font-medium">{a.tenant_name}</div>
                          {a.has_pending_upgrade && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              <span><span key={tUi("pending")}>{tUi("pending")}</span></span> {a.pending_concurrency}<span>/</span>{a.pending_agents}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{a.reserved_concurrency}</TableCell>
                        <TableCell>
                          <div>{a.real_agents}</div>
                          <div className="text-xs text-muted-foreground">
                            {a.reserved_agents} <span><span key={tUi("reserved")}>{tUi("reserved")}</span></span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassign(a.tenant_id)}
                            disabled={saving}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Assign new tenant */}
            <div className="space-y-3 border-t pt-4">
              <h4 className="text-sm font-medium"><span key={tUi("assignTenant")}>{tUi("assignTenant")}</span></h4>
              <p className="text-xs text-muted-foreground">
                <span key={tUi("reservedSlotsAreDerivedFromTheTenantS")}>{tUi("reservedSlotsAreDerivedFromTheTenantS")}</span></p>
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs"><span key={tUi("tenant")}>{tUi("tenant")}</span></Label>
                  <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                    <SelectTrigger>
                      <SelectValue placeholder={tUi("selectTenant")} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span>{t.name}</span>
                          {t.current_account_name && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              <ArrowRight className="inline h-3 w-3 mx-0.5" />
                              {t.current_account_name}
                            </span>
                          )}
                        </SelectItem>
                      ))}
                      {availableTenants.length === 0 && (
                        <SelectItem value="_none" disabled>
                          <span key={tUi("noTenantsAvailable")}>{tUi("noTenantsAvailable")}</span></SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  onClick={handleAssign}
                  disabled={saving || !selectedTenantId}
                  className="h-9"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {selectedTenantId && (() => {
                const tenant = tenantOptions.find((t) => t.id === selectedTenantId)
                if (tenant?.current_account_name) {
                  return (
                    <p className="text-xs text-amber-600">
                      <span><span key={tUi("thisTenantWillBeReassignedFrom")}>{tUi("thisTenantWillBeReassignedFrom")}</span></span>{tenant.current_account_name}<span>&quot;</span>
                    </p>
                  )
                }
                return null
              })()}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
